/**
 * Why the stored data dictionary is the size it is (Jim's item 2, 2026-10-01).
 *
 * A dictionary built from the forms reads approved forms only, lists a grid's
 * analytes on one entry per column, and leaves out derived, system and coded
 * fields, so it can be much smaller than a sponsor's own file. When a sponsor
 * file was adopted, its rows can also be missing because they were not chosen,
 * needed review, could not be used, or were removed when the dictionary was
 * regenerated. The Data Dictionary card on Study Documents said only "Version
 * N, X fields from Y forms". This turns what is stored into plain sentences.
 *
 * Reads:
 *  - the dictionary's main document: fieldCount, formCount, version,
 *    gridSummary, sponsorFields (functions/src/dataDictionary/sponsorDictionaryCounts.ts),
 *    whose byAdoption says what is left of each adoption now;
 *  - dataManagement/dictionaryAdoption: completedAt and lastCompleted (the
 *    stored counts of the last completed adoption);
 *  - optionally the adoption history record, for an adoption completed before
 *    lastCompleted was written.
 *
 * Pure, so it is tested from functions/vitest.web.config.ts. The adopt result
 * screen reads its not-stored breakdown through `notStoredBreakdown`, so the
 * screen and the card cannot count differently.
 */

export interface NotStoredReasonsLike {
  notChosen: number;
  neededReview: number;
  couldNotBeUsed: number;
}

export interface NotStoredBreakdown extends NotStoredReasonsLike {
  total: number;
  /** Rows not stored for which no reason was recorded (an older adoption). */
  unexplained: number;
}

const n0 = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) && v > 0 ? Math.floor(v) : 0);
const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

/** The rows of a file that were not stored, split by reason, each counted once. */
export function notStoredBreakdown(entriesNotStored: unknown, reasons: Partial<NotStoredReasonsLike> | null | undefined): NotStoredBreakdown {
  const total = n0(entriesNotStored);
  const notChosen = n0(reasons?.notChosen);
  const neededReview = n0(reasons?.neededReview);
  const couldNotBeUsed = n0(reasons?.couldNotBeUsed);
  return {
    total, notChosen, neededReview, couldNotBeUsed,
    unexplained: Math.max(0, total - notChosen - neededReview - couldNotBeUsed),
  };
}

interface StoredAdoptionCounts {
  historyId?: string;
  fileRowCount?: number;
  entriesStored?: number;
  entriesNotStored?: number;
  entriesUnlinked?: number;
  notStoredReasons?: Partial<NotStoredReasonsLike> | null;
}

export interface DictionarySizeInput {
  dictionary: {
    version?: number;
    fieldCount?: number;
    formCount?: number;
    gridSummary?: { gridColumns?: number; gridRows?: number } | null;
    sponsorFields?: {
      fromSponsor?: number; latestHistoryId?: string | null; fromLatestAdoption?: number;
      /** Per adoption, what is stored now (functions/src/dataDictionary/sponsorDictionaryCounts.ts). */
      byAdoption?: Array<{ historyId?: string; entries?: number; unlinked?: number } | null> | null;
    } | null;
  } | null | undefined;
  adoption?: {
    completedAt?: unknown;
    historyId?: string;
    lastCompleted?: StoredAdoptionCounts | null;
  } | null;
  /** The adoption history record, used only when the adoption document has no lastCompleted. */
  history?: (StoredAdoptionCounts & { completedAt?: unknown }) | null;
  /** The live number of entries, when the caller has it; wins over the stored fieldCount. */
  entryCount?: number;
}

export interface DictionarySizeSummary {
  /** A sponsor's dictionary has been adopted, to completion. */
  sponsorInPlay: boolean;
  /** Rows in the sponsor's file, when known. */
  sponsorFileRows: number | null;
  fieldCount: number;
  formCount: number;
  /** Of the fields, how many came from the sponsor's file; null when not recorded. */
  fromSponsor: number | null;
  gridColumns: number;
  gridAnalytes: number;
  /**
   * Rows of the sponsor's file that are not in the dictionary, by reason; null
   * when nothing stored says what describes the current dictionary.
   */
  notInDictionary: (NotStoredBreakdown & { removedAtRegeneration: number }) | null;
  /** Entries from the file, stored now, with no link to a form; null when not known. */
  unlinked: number | null;
  /** The card's summary line, or null when there is no dictionary. */
  line: string | null;
  /** Plain-language sentences explaining a dictionary smaller than the sponsor's file. */
  warnings: string[];
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

export function summarizeDictionarySize(input: DictionarySizeInput): DictionarySizeSummary {
  const dd = input?.dictionary || null;
  const fieldCount = isNum(input?.entryCount) ? n0(input.entryCount) : n0(dd?.fieldCount);
  const formCount = n0(dd?.formCount);
  const gridColumns = n0(dd?.gridSummary?.gridColumns);
  const gridAnalytes = gridColumns > 0 ? n0(dd?.gridSummary?.gridRows) : 0;

  // The last completed adoption's counts: the adoption document's copy, else
  // the history record (an adoption completed before the copy was written).
  const adoption = input?.adoption || null;
  let counts: StoredAdoptionCounts | null = null;
  if (adoption?.completedAt) {
    if (adoption.lastCompleted) counts = adoption.lastCompleted;
    else if (input.history?.completedAt) counts = { historyId: adoption.historyId, ...input.history };
  }
  const sponsorInPlay = !!counts && isNum(counts.entriesStored);

  let sponsorFileRows: number | null = null;
  if (sponsorInPlay) {
    if (isNum(counts!.fileRowCount)) sponsorFileRows = n0(counts!.fileRowCount);
    else if (isNum(counts!.entriesNotStored)) sponsorFileRows = n0(counts!.entriesStored) + n0(counts!.entriesNotStored);
  }

  const sf = dd?.sponsorFields;
  const fromSponsor = sponsorInPlay && sf && isNum(sf.fromSponsor) ? n0(sf.fromSponsor) : null;

  // What is left of the adoption NOW, from the per-adoption counts the latest
  // dictionary write (adoption or regeneration) stored. The adoption's own
  // counts describe the dictionary it produced, not the one stored after a
  // regeneration, so they are never used alone (checker B1/B2, 2026-10-01).
  // Unknown, and so not printed, when:
  //  - the write predates the per-adoption counts (no byAdoption): a legacy
  //    adoption, or a regeneration by an older build;
  //  - a newer adoption was started and did not complete (dictionaryAdoption's
  //    historyId is not the completed one's): its entries may be stored, and
  //    the completed adoption's counts would describe a dictionary that is gone.
  const survivorRows = Array.isArray(sf?.byAdoption) ? sf!.byAdoption : null;
  const latestAttempt = adoption?.historyId;
  const countsAreCurrent = sponsorInPlay && !!counts!.historyId && survivorRows !== null
    && (!latestAttempt || latestAttempt === counts!.historyId);
  let notInDictionary: DictionarySizeSummary['notInDictionary'] = { ...notStoredBreakdown(0, null), removedAtRegeneration: 0 };
  let unlinked: number | null = 0;
  if (sponsorInPlay && !countsAreCurrent) {
    notInDictionary = null;
    unlinked = null;
  } else if (sponsorInPlay) {
    // An adoption with nothing left is absent from the list: zero survivors.
    const left = survivorRows!.find((r) => r && r.historyId === counts!.historyId);
    const removedAtRegeneration = Math.max(0, n0(counts!.entriesStored) - n0(left?.entries));
    const base = notStoredBreakdown(counts!.entriesNotStored, counts!.notStoredReasons);
    notInDictionary = { ...base, total: base.total + removedAtRegeneration, removedAtRegeneration };
    unlinked = n0(left?.unlinked);
  }

  const warnings: string[] = [];
  // No dictionary document, no warning: there is nothing for it to describe.
  const smaller = !!dd && sponsorInPlay && sponsorFileRows !== null && fieldCount < sponsorFileRows;
  if (smaller) {
    let first = `This dictionary has ${plural(fieldCount, 'field', 'fields')}`;
    if (fromSponsor !== null && fromSponsor > 0) {
      first += fromSponsor >= fieldCount ? ", all from the sponsor's file" : `, ${fromSponsor} of them from the sponsor's file`;
    }
    warnings.push(`${first}.`);

    let second = `The sponsor's file had ${plural(sponsorFileRows!, 'row', 'rows')}`;
    // Not known for the current dictionary: the file's size alone, no count.
    const d = notInDictionary;
    if (d && d.total > 0) {
      second += `: ${d.total} of them ${d.total === 1 ? 'is' : 'are'} not in it`;
      const parts: string[] = [];
      if (d.notChosen > 0) parts.push(`${d.notChosen} not chosen`);
      if (d.neededReview > 0) parts.push(`${d.neededReview} needed review and ${d.neededReview === 1 ? 'was' : 'were'} not accepted`);
      if (d.couldNotBeUsed > 0) parts.push(`${d.couldNotBeUsed} could not be used`);
      if (d.removedAtRegeneration > 0) parts.push(`${d.removedAtRegeneration} removed when the dictionary was regenerated`);
      // A reason is only worth naming as "not recorded" beside reasons that are.
      if (d.unexplained > 0 && parts.length > 0) parts.push(`${d.unexplained} for a reason not recorded`);
      if (parts.length > 0) second += ` (${parts.join(', ')})`;
    }
    warnings.push(`${second}.`);

    if (gridColumns > 0) {
      warnings.push(
        `Grid forms list their analytes on one entry per column: ${plural(gridColumns, 'column covers', 'columns cover')} ${plural(gridAnalytes, 'analyte', 'analytes')}.`,
      );
    }
  }
  if (!!dd && sponsorInPlay && unlinked !== null && unlinked > 0) {
    warnings.push(
      `${plural(unlinked, 'entry is', 'entries are')} not linked to a form, so documents built from the dictionary may leave ${unlinked === 1 ? 'it' : 'them'} out.`,
    );
  }

  let line: string | null = null;
  if (fieldCount > 0) {
    line = `Version ${n0(dd?.version) || 1}: ${plural(fieldCount, 'field', 'fields')} from ${plural(formCount, 'form', 'forms')}`;
    // When the warning shows, its own sentence says this about the grids.
    if (gridColumns > 0 && !smaller) {
      line += gridColumns === 1
        ? `, of which 1 is a grid column covering ${plural(gridAnalytes, 'analyte', 'analytes')}`
        : `, of which ${gridColumns} are grid columns covering ${plural(gridAnalytes, 'analyte', 'analytes')}`;
    }
  }

  return {
    sponsorInPlay, sponsorFileRows, fieldCount, formCount, fromSponsor, gridColumns, gridAnalytes,
    notInDictionary, unlinked, line, warnings,
  };
}
